/**
 * Toolbar for the CDM WYSIWYG editor (Part 1.8 of the plan). Unlike NewTextTemplatePage.tsx's
 * Toolbar (which drives Tiptap's stock bold/italic/color/etc. marks), every button here reads and
 * writes into the whole-value-object attr blobs schema.ts's "attrs ARE the model" design uses —
 * see that file's header comment. Shares the generic, command-agnostic UI chrome
 * (TBtn/Sep/ColorBtn/TableInsertBtn/SymbolBtn/PopoverBtn) with the legacy toolbar via
 * ToolbarControls.tsx.
 */
import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faAlignLeft, faAlignCenter, faAlignRight, faAlignJustify, faPlus, faMinus, faTrash, faLink,
  faLinkSlash, faImage, faRotateLeft, faRotateRight, faSuperscript, faSubscript, faRulerHorizontal,
  faIndent, faOutdent, faListUl, faListOl, faSpinner,
} from '@fortawesome/free-solid-svg-icons'
import type { useEditor } from '@tiptap/react'
import { useEditorState as useEditorStateHook } from '@tiptap/react'
import { colors, fontSize, radius } from '../../styles/theme'
import { Select } from '../ui'
import {
  TBtn, Sep, ColorBtn, TableInsertBtn, PopoverBtn,
} from './ToolbarControls'
import {
  HAlign, UnderlineStyle, VerticalScript,
  defaultRunFormat, defaultParagraphFormat, defaultSourceLocation,
  type RunFormat, type ParagraphFormat, type Footnote, type Endnote,
} from '../../cdm/types'
import { normalizeMonikerKey } from '../../cdm/monikerKey'
import { uploadAndInsertImage } from './imageUpload'
import { NodeSelection } from '@tiptap/pm/state'
import { isFloatingImage, makeImageInline, floatingTextBoxPos, makeTextBoxInline, type ImageAttrs } from '../../cdm/schema'
import PageSetupBtn from './PageSetupBtn'
import HeaderFooterBtn from './HeaderFooterBtn'
import FootnoteEndnoteBtn from './FootnoteEndnoteBtn'
import ColumnLoopDialog from './ColumnLoopDialog'

type CdmEditor = ReturnType<typeof useEditor>

const FONT_FAMILIES = [
  { value: 'Calibri', label: 'Calibri' },
  { value: 'Arial', label: 'Arial' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Times New Roman', label: 'Times New Roman' },
  { value: 'Courier New', label: 'Courier New' },
  { value: 'Verdana', label: 'Verdana' },
]

const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48].map(pt => ({ value: String(pt), label: String(pt) }))

function activeParagraphType(editor: CdmEditor): 'paragraph' | 'heading' {
  return editor?.isActive('heading') ? 'heading' : 'paragraph'
}

/**
 * Inserting a BLOCK-level node (list/loop/conditional/pageBreak/image) via plain `insertContent`
 * while the cursor sits mid-paragraph is ambiguous in ProseMirror — a block node doesn't fit
 * directly at an inline position, and letting `insertContent` guess how to resolve that produced
 * two different failure modes when tested live: it silently no-op'd for `list`, and it fell back
 * to inserting at the very start of the document for `loop`/`image` (all reproduced by placing
 * the cursor mid-paragraph and clicking the button — the toolbar's own focus-stealing wasn't the
 * cause; ProseMirror's selection persists across DOM focus changes). `$from.after()` resolves to
 * the position right after whichever block-level ancestor directly contains the cursor (the
 * paragraph/heading/list-item/etc.), giving `insertContentAt` an unambiguous block boundary to
 * insert at instead — the new block always lands as the next sibling of the block the cursor was
 * in, which is what every toolbar button here is documented/expected to do. Module-scoped (not a
 * closure inside CdmToolbar) so ImageBtn can also capture a position synchronously before its own
 * async upload — by the time an `await`'d upload resolves, the editor's selection may have moved
 * on (e.g. focus went to the popover's alt-text field), so ImageBtn resolves `pos` up front and
 * passes it through rather than re-reading `editor.state.selection` after the await.
 */
/** A floating picture is selected — the only time "Make inline" is offered. */
function selectedFloatingImage(editor: CdmEditor | null | undefined): boolean {
  const sel = editor?.state.selection
  return sel instanceof NodeSelection && sel.node.type.name === 'image' && isFloatingImage(sel.node.attrs as Partial<ImageAttrs>)
}

function insertBlockAfterCursor(editor: CdmEditor, content: object, atPos?: number) {
  const pos = atPos ?? editor!.state.selection.$from.after()
  editor!.chain().focus().insertContentAt(pos, content).run()
}

interface CdmToolbarProps {
  editor: CdmEditor
  templateId: string
  footnotes: Footnote[]
  endnotes: Endnote[]
  onFootnotesChange: (footnotes: Footnote[]) => void
  onEndnotesChange: (endnotes: Endnote[]) => void
}

export default function CdmToolbar({ editor, templateId, footnotes, endnotes, onFootnotesChange, onEndnotesChange }: CdmToolbarProps) {
  const [varName, setVarName] = useState('')
  const [loopName, setLoopName] = useState('')
  const [loopAlias, setLoopAlias] = useState('item')
  const [condName, setCondName] = useState('')
  const [columnLoopOpen, setColumnLoopOpen] = useState(false)

  const editorState = useEditorStateHook({
    editor,
    selector: e => ({
      inTable: e.editor?.isActive('table') ?? false,
      runFormat: (e.editor?.getAttributes('runFormat').format as RunFormat | undefined) ?? defaultRunFormat(),
      paraFormat: (e.editor?.getAttributes(activeParagraphType(e.editor)).format as ParagraphFormat | undefined) ?? defaultParagraphFormat(),
      isH1: e.editor?.isActive('heading', { level: 1 }) ?? false,
      isH2: e.editor?.isActive('heading', { level: 2 }) ?? false,
      isH3: e.editor?.isActive('heading', { level: 3 }) ?? false,
      isListItem: e.editor?.isActive('listItem') ?? false,
      floatingImage: selectedFloatingImage(e.editor),
      floatingTextBox: e.editor ? floatingTextBoxPos(e.editor.state) >= 0 : false,
    }),
  })

  if (!editor) return null

  const { inTable, runFormat, paraFormat, isH1, isH2, isH3, isListItem, floatingImage, floatingTextBox } = editorState ?? {}

  function setRunFormat(patch: Partial<RunFormat>) {
    editor!.chain().focus().setMark('runFormat', { format: { ...(runFormat ?? defaultRunFormat()), ...patch } }).run()
  }

  function setParaFormat(patch: Partial<ParagraphFormat>) {
    editor!.chain().focus().updateAttributes(activeParagraphType(editor), { format: { ...(paraFormat ?? defaultParagraphFormat()), ...patch } }).run()
  }

  function setHeadingLevel(level: number) {
    editor!.chain().focus().setNode('heading', { level, format: paraFormat ?? defaultParagraphFormat() }).run()
  }

  function setNormalText() {
    editor!.chain().focus().setNode('paragraph', { format: paraFormat ?? defaultParagraphFormat() }).run()
  }

  function insertList(ordered: boolean) {
    insertBlockAfterCursor(editor, {
      type: 'list',
      attrs: { ordered },
      content: [{ type: 'listItem', attrs: { level: 0 }, content: [{ type: 'paragraph', attrs: { format: defaultParagraphFormat() }, content: [] }] }],
    })
  }

  function adjustListItemLevel(delta: 1 | -1) {
    const { state, dispatch } = editor!.view
    const tr = state.tr
    const { from, to } = state.selection
    state.doc.nodesBetween(from, to, (node, pos) => {
      if (node.type.name === 'listItem') {
        const cur = (node.attrs.level as number) || 0
        const next = Math.min(Math.max(cur + delta, 0), 8)
        if (next !== cur) tr.setNodeMarkup(pos, undefined, { ...node.attrs, level: next })
      }
    })
    dispatch(tr)
  }

  function insertPlaceholder() {
    const rawName = varName.trim()
    if (!rawName) return
    editor!.chain().focus().insertContent({
      type: 'placeholder',
      attrs: { rawName, normalizedKey: normalizeMonikerKey(rawName), formatters: null, format: defaultRunFormat(), source: defaultSourceLocation() },
    }).run()
    setVarName('')
  }

  function insertLoop() {
    const rawName = loopName.trim()
    if (!rawName) return
    insertBlockAfterCursor(editor, {
      type: 'loop',
      attrs: { rawName, normalizedKey: normalizeMonikerKey(rawName), itemAlias: loopAlias.trim() || 'item', source: defaultSourceLocation() },
      content: [{ type: 'paragraph', attrs: { format: defaultParagraphFormat() }, content: [] }],
    })
    setLoopName('')
  }

  function insertConditional(withElse: boolean) {
    const rawName = condName.trim()
    if (!rawName) return
    const branches = [{ type: 'branch', attrs: { kind: 'body' }, content: [{ type: 'paragraph', attrs: { format: defaultParagraphFormat() }, content: [] }] }]
    if (withElse) branches.push({ type: 'branch', attrs: { kind: 'else' }, content: [{ type: 'paragraph', attrs: { format: defaultParagraphFormat() }, content: [] }] })
    insertBlockAfterCursor(editor, {
      type: 'conditional',
      attrs: { rawName, normalizedKey: normalizeMonikerKey(rawName), negate: false, source: defaultSourceLocation() },
      content: branches,
    })
    setCondName('')
  }

  return (
    <div style={{ border: `1px solid ${colors.border}`, borderBottom: 'none', borderRadius: `${radius.sm} ${radius.sm} 0 0`, background: colors.background }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', padding: '6px 8px', rowGap: 6 }}>
        <TBtn active={false} onClick={() => editor.chain().focus().undo().run()} title="Undo">
          <FontAwesomeIcon icon={faRotateLeft} />
        </TBtn>
        <TBtn active={false} onClick={() => editor.chain().focus().redo().run()} title="Redo">
          <FontAwesomeIcon icon={faRotateRight} />
        </TBtn>

        <Sep />

        <div style={{ width: 150 }}>
          <Select
            inputSize="sm"
            placeholder="Font"
            value={runFormat?.fontFamily ?? null}
            onChange={v => setRunFormat({ fontFamily: v ? String(v) : null })}
            options={FONT_FAMILIES}
          />
        </div>
        <div style={{ width: 72 }}>
          <Select
            inputSize="sm"
            placeholder="Size"
            value={runFormat?.fontSizePt != null ? String(runFormat.fontSizePt) : null}
            onChange={v => setRunFormat({ fontSizePt: v ? Number(v) : null })}
            options={FONT_SIZES}
          />
        </div>

        <Sep />

        <TBtn active={runFormat?.bold} onClick={() => setRunFormat({ bold: !runFormat?.bold })} title="Bold"><b>B</b></TBtn>
        <TBtn active={runFormat?.italic} onClick={() => setRunFormat({ italic: !runFormat?.italic })} title="Italic"><em>I</em></TBtn>
        <TBtn active={runFormat?.underline !== UnderlineStyle.None} onClick={() => setRunFormat({ underline: runFormat?.underline === UnderlineStyle.None ? UnderlineStyle.Single : UnderlineStyle.None })} title="Underline"><u>U</u></TBtn>
        <TBtn active={runFormat?.strike} onClick={() => setRunFormat({ strike: !runFormat?.strike })} title="Strikethrough"><s>S</s></TBtn>
        <TBtn active={runFormat?.script === VerticalScript.Superscript} onClick={() => setRunFormat({ script: runFormat?.script === VerticalScript.Superscript ? VerticalScript.Baseline : VerticalScript.Superscript })} title="Superscript">
          <FontAwesomeIcon icon={faSuperscript} />
        </TBtn>
        <TBtn active={runFormat?.script === VerticalScript.Subscript} onClick={() => setRunFormat({ script: runFormat?.script === VerticalScript.Subscript ? VerticalScript.Baseline : VerticalScript.Subscript })} title="Subscript">
          <FontAwesomeIcon icon={faSubscript} />
        </TBtn>

        <Sep />

        <TBtn active={isH1} onClick={() => setHeadingLevel(1)} title="Heading 1">H1</TBtn>
        <TBtn active={isH2} onClick={() => setHeadingLevel(2)} title="Heading 2">H2</TBtn>
        <TBtn active={isH3} onClick={() => setHeadingLevel(3)} title="Heading 3">H3</TBtn>
        <TBtn active={!isH1 && !isH2 && !isH3} onClick={setNormalText} title="Normal text">¶</TBtn>

        <Sep />

        <TBtn active={false} onClick={() => insertList(false)} title="Insert bullet list"><FontAwesomeIcon icon={faListUl} /></TBtn>
        <TBtn active={false} onClick={() => insertList(true)} title="Insert numbered list"><FontAwesomeIcon icon={faListOl} /></TBtn>
        <TBtn disabled={!isListItem} active={false} onClick={() => adjustListItemLevel(1)} title="Indent list item"><FontAwesomeIcon icon={faIndent} /></TBtn>
        <TBtn disabled={!isListItem} active={false} onClick={() => adjustListItemLevel(-1)} title="Outdent list item"><FontAwesomeIcon icon={faOutdent} /></TBtn>

        <Sep />

        <TBtn active={paraFormat?.alignment === HAlign.Left} onClick={() => setParaFormat({ alignment: HAlign.Left })} title="Align left"><FontAwesomeIcon icon={faAlignLeft} /></TBtn>
        <TBtn active={paraFormat?.alignment === HAlign.Center} onClick={() => setParaFormat({ alignment: HAlign.Center })} title="Align center"><FontAwesomeIcon icon={faAlignCenter} /></TBtn>
        <TBtn active={paraFormat?.alignment === HAlign.Right} onClick={() => setParaFormat({ alignment: HAlign.Right })} title="Align right"><FontAwesomeIcon icon={faAlignRight} /></TBtn>
        <TBtn active={paraFormat?.alignment === HAlign.Justify} onClick={() => setParaFormat({ alignment: HAlign.Justify })} title="Justify"><FontAwesomeIcon icon={faAlignJustify} /></TBtn>

        <Sep />

        <ColorBtn
          tooltipText="Font color"
          labelContent={<span style={{ fontWeight: 700, borderBottom: '2px solid currentColor' }}>A</span>}
          onChange={color => setRunFormat({ colorHex: color })}
        />
        <ColorBtn
          tooltipText="Highlight color"
          labelContent={<span style={{ background: '#fef08a', padding: '0 3px', borderRadius: 2, fontWeight: 700 }}>A</span>}
          defaultValue="#fef08a"
          onChange={color => setRunFormat({ highlightHex: color })}
        />

        <Sep />

        <TBtn active={false} onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal rule">
          <FontAwesomeIcon icon={faRulerHorizontal} />
        </TBtn>
        <TBtn active={false} onClick={() => insertBlockAfterCursor(editor, { type: 'pageBreak' })} title="Page break">
          Page break
        </TBtn>

        <Sep />

        <LinkBtn editor={editor} />
        <ImageBtn editor={editor} templateId={templateId} />
        {floatingImage && (
          <TBtn active={false} onClick={() => makeImageInline(editor)} title="Make inline: drop this picture's floating position and wrap, and place it in the text">
            Make inline
          </TBtn>
        )}
        {!floatingImage && floatingTextBox && (
          <TBtn active={false} onClick={() => makeTextBoxInline(editor)} title="Make inline: drop this text box's floating position and wrap, and place it in the text">
            Make inline
          </TBtn>
        )}
        <PageSetupBtn editor={editor} />
        <HeaderFooterBtn mainEditor={editor} />
        <FootnoteEndnoteBtn
          mainEditor={editor}
          footnotes={footnotes}
          endnotes={endnotes}
          onFootnotesChange={onFootnotesChange}
          onEndnotesChange={onEndnotesChange}
        />
      </div>

      {/* Template / variable row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', padding: '5px 8px', borderTop: `1px solid ${colors.border}`, rowGap: 4 }}>
        <span style={{ fontSize: fontSize.xs, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginRight: 4 }}>Variable</span>
        <input
          type="text"
          value={varName}
          onChange={e => setVarName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); insertPlaceholder() } }}
          placeholder="var_name"
          style={{ padding: '3px 6px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, width: 100, outline: 'none' }}
        />
        <button type="button" onClick={insertPlaceholder} style={{ padding: '3px 8px', border: `1px solid ${colors.primary}`, borderRadius: radius.sm, background: colors.primaryLight, color: colors.primary, fontSize: fontSize.sm, fontWeight: 600, cursor: 'pointer' }}>Insert</button>

        <Sep />

        <span style={{ fontSize: fontSize.xs, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginRight: 4 }}>Loop</span>
        <input type="text" value={loopName} onChange={e => setLoopName(e.target.value)} placeholder="collection" style={{ padding: '3px 6px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, width: 90, outline: 'none' }} />
        <span style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>as</span>
        <input type="text" value={loopAlias} onChange={e => setLoopAlias(e.target.value)} placeholder="item" style={{ padding: '3px 6px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, width: 60, outline: 'none' }} />
        <button type="button" onClick={insertLoop} style={{ padding: '3px 8px', border: `1px solid ${colors.primary}`, borderRadius: radius.sm, background: colors.primaryLight, color: colors.primary, fontSize: fontSize.sm, fontWeight: 600, cursor: 'pointer' }}>Insert</button>

        <Sep />

        <span style={{ fontSize: fontSize.xs, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginRight: 4 }}>If</span>
        <input type="text" value={condName} onChange={e => setCondName(e.target.value)} placeholder="condition" style={{ padding: '3px 6px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, width: 90, outline: 'none' }} />
        <button type="button" onClick={() => insertConditional(false)} style={{ padding: '3px 8px', border: `1px solid ${colors.primary}`, borderRadius: radius.sm, background: colors.primaryLight, color: colors.primary, fontSize: fontSize.sm, fontWeight: 600, cursor: 'pointer' }}>Insert</button>
        <button type="button" onClick={() => insertConditional(true)} style={{ padding: '3px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: 'none', color: colors.text, fontSize: fontSize.sm, cursor: 'pointer' }}>+ else</button>
      </div>

      {/* Table row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', padding: '5px 8px', borderTop: `1px solid ${colors.border}`, rowGap: 4 }}>
        <span style={{ fontSize: fontSize.xs, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginRight: 4 }}>Table</span>
        <TableInsertBtn onInsert={(rows, cols) => editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run()} />
        <Sep />
        <TBtn disabled={!inTable} active={false} onClick={() => editor.chain().focus().addColumnBefore().run()} title="Add column before"><FontAwesomeIcon icon={faPlus} />&nbsp;col←</TBtn>
        <TBtn disabled={!inTable} active={false} onClick={() => editor.chain().focus().addColumnAfter().run()} title="Add column after"><FontAwesomeIcon icon={faPlus} />&nbsp;col→</TBtn>
        <TBtn disabled={!inTable} active={false} onClick={() => editor.chain().focus().deleteColumn().run()} title="Delete column"><FontAwesomeIcon icon={faMinus} />&nbsp;col</TBtn>
        <Sep />
        <TBtn disabled={!inTable} active={false} onClick={() => editor.chain().focus().addRowBefore().run()} title="Add row above"><FontAwesomeIcon icon={faPlus} />&nbsp;row↑</TBtn>
        <TBtn disabled={!inTable} active={false} onClick={() => editor.chain().focus().addRowAfter().run()} title="Add row below"><FontAwesomeIcon icon={faPlus} />&nbsp;row↓</TBtn>
        <TBtn disabled={!inTable} active={false} onClick={() => editor.chain().focus().deleteRow().run()} title="Delete row"><FontAwesomeIcon icon={faMinus} />&nbsp;row</TBtn>
        <Sep />
        <TBtn disabled={!inTable} active={false} onClick={() => editor.chain().focus().mergeCells().run()} title="Merge cells">merge</TBtn>
        <TBtn disabled={!inTable} active={false} onClick={() => editor.chain().focus().splitCell().run()} title="Split cell">split</TBtn>
        <Sep />
        <TBtn disabled={!inTable} active={false} onClick={() => setColumnLoopOpen(true)} title="Repeat the selected columns for each item of a collection ({{#columns}})">column loop</TBtn>
        <Sep />
        <TBtn disabled={!inTable} active={false} onClick={() => editor.chain().focus().deleteTable().run()} title="Delete table"><FontAwesomeIcon icon={faTrash} /></TBtn>
      </div>
      {columnLoopOpen && <ColumnLoopDialog editor={editor} onClose={() => setColumnLoopOpen(false)} />}
    </div>
  )
}

/**
 * HyperlinkRun is an inline atom, not a mark (see schema.ts) — inserting one replaces the
 * selection rather than toggling a mark on it. Uses the current selection's plain text as the
 * link's Text, or the URL itself when nothing is selected.
 *
 * Guarded against a real incident: `textBetween(from, to, ' ')` flattens text across BLOCK
 * boundaries too (joining with a space), so a selection spanning more than one paragraph/
 * heading/table-cell — not just multiple runs within one — would get its entire multi-block
 * structure collapsed into a single inline atom's flat `text` attribute, silently destroying
 * every paragraph break, heading, and table cell caught in the selection. `sameParent` restricts
 * this to a selection that stays within one block, which is the only case where flattening to a
 * single run is actually safe (a HyperlinkRun losing sub-span formatting within one paragraph is
 * the documented, deliberate trade-off of modeling it as an atom — losing whole blocks is not).
 */
function LinkBtn({ editor }: { editor: CdmEditor }) {
  const [url, setUrl] = useState('')
  const [spansBlocks, setSpansBlocks] = useState(false)
  const active = editor?.isActive('hyperlinkRun') ?? false

  function apply() {
    const href = url.trim()
    if (!href || !editor) return
    const { $from, $to, from, to } = editor.state.selection
    if (!$from.sameParent($to)) {
      setSpansBlocks(true)
      return
    }
    const text = editor.state.doc.textBetween(from, to, ' ') || href
    editor.chain().focus().insertContent({
      type: 'hyperlinkRun',
      attrs: { text, url: href, anchorName: null, format: defaultRunFormat() },
    }).run()
    setUrl('')
    setSpansBlocks(false)
  }

  return (
    <PopoverBtn icon={faLink} activeIcon={faLinkSlash} tooltipText="Insert link" active={active} onActivate={() => setSpansBlocks(false)} onDeactivate={() => {}}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <input
          autoFocus
          type="url"
          value={url}
          onChange={e => { setUrl(e.target.value); setSpansBlocks(false) }}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); apply() } }}
          placeholder="https://example.com"
          style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, outline: 'none', width: '100%', boxSizing: 'border-box' }}
        />
        {spansBlocks && (
          <div style={{ color: colors.error, fontSize: fontSize.xs }}>
            Select text within a single paragraph to link it.
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" onClick={apply} style={{ padding: '4px 12px', background: colors.primary, color: '#fff', border: 'none', borderRadius: radius.sm, fontSize: fontSize.sm, fontWeight: 600, cursor: 'pointer' }}>
            Insert link
          </button>
        </div>
      </div>
    </PopoverBtn>
  )
}

/**
 * Image insertion for the CDM editor is upload-only (no URL tab, unlike NewTextTemplatePage's
 * ImageBtn) — ImageBlock.blobRef must reference an asset in the template's blob storage, so an
 * arbitrary external URL has nowhere to live in the model. Uploads via upload-asset, then inserts
 * an `image` node using an object URL for immediate display (resolvedSource is re-fetched from
 * the real asset endpoint whenever the document is reloaded — see TemplateEditorPage.tsx).
 */
function ImageBtn({ editor, templateId }: { editor: CdmEditor; templateId: string }) {
  const [uploading, setUploading] = useState(false)
  const [altText, setAltText] = useState('')
  const fileRef = React.useRef<HTMLInputElement>(null)

  function handleFile(file: File) {
    // Resolved now, synchronously, rather than re-reading editor.state.selection once the
    // upload's await resolves — by then the selection may have moved on (e.g. focus went to this
    // popover's alt-text field), which is exactly the "insert at the wrong place" failure mode
    // insertBlockAfterCursor's own doc comment describes for list/loop/conditional/pageBreak.
    const pos = editor!.state.selection.$from.after()
    setUploading(true)
    uploadAndInsertImage(editor!, templateId, file, pos, altText)
      .then(() => setAltText(''))
      .catch(() => {})
      .finally(() => setUploading(false))
  }

  return (
    <PopoverBtn icon={faImage} tooltipText="Insert image" active={false} onActivate={() => {}} onDeactivate={() => {}}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 260 }}>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f) }} />
        <div
          onClick={() => fileRef.current?.click()}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '20px 12px', border: `2px dashed ${colors.border}`, borderRadius: radius.md, background: colors.background, cursor: 'pointer', userSelect: 'none' }}
        >
          <FontAwesomeIcon icon={uploading ? faSpinner : faImage} spin={uploading} style={{ fontSize: 24, color: colors.textSecondary }} />
          <span style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>{uploading ? 'Uploading…' : 'Click to choose an image'}</span>
        </div>
        <input
          type="text"
          value={altText}
          onChange={e => setAltText(e.target.value)}
          placeholder="Alt text (optional)"
          style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, outline: 'none', width: '100%', boxSizing: 'border-box' }}
        />
      </div>
    </PopoverBtn>
  )
}
